import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// @ts-ignore - TypeScript casing issue with recoil import
import { useRecoilValue } from 'recoil';
import Layout from '../../../../components/Learner/Layout';
import WritingPracticeComponent from '../../../../components/Learner/WritingPractice';
import { getWritingPracticeQuestions, submitPractice, getPracticeStatus } from '../../../../services/practiceApi';
import { usePolling } from '../../../../hooks/usePolling';
// @ts-ignore - TypeScript casing issue with atoms import
import { courseTreeAtom, courseTreeBaselineAtom } from '../../../../recoil/atoms';
import { findLangCodeBySubcourseIdWithFallback } from '../../../../utils/courseTreeUtils';
import { 
  getPracticeParams as getPracticeParamsUtil, 
  formatWritingUserAnswers,
  buildResultPageState,
  clearPracticeStorage,
} from '../../../../utils/practiceUtils';
import { usePracticeSubmission, usePollingResult } from '../../../../hooks/usePracticeSubmission';
import { usePracticeSession, isPageReload } from '../../../../hooks/usePracticeSession';
import {
  PracticeErrorState,
  PracticeNoQuestionsState,
} from '../../../../components/Learner/Practice/PracticePageStates';
import PracticeLoadingOverlay from '../../../../components/Learner/Practice/PracticeLoadingOverlay';
import { getLastOpenedUrl } from '../../../../utils/lastOpened';
import './QuickPractice.css';

const WritingPractice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const courseTree = useRecoilValue(courseTreeAtom);
  const courseTreeBaseline = useRecoilValue(courseTreeBaselineAtom);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);
  const [pollingRetryCount, setPollingRetryCount] = useState(0);
  const [gradingFatalError, setGradingFatalError] = useState(null);
  const requestIdRef = useRef(null);

  const { saveSession, loadSession, clearSession } = usePracticeSession('writing-practice');

  // changed by Cursor: sync ref immediately so polling can use it before re-render
  const setRequestIdWithRef = useCallback((id) => {
    requestIdRef.current = id;
    setRequestId(id);
  }, []);

  // Get practice parameters from location state or URL params
  const getPracticeParams = () => getPracticeParamsUtil(location);

  // Fetch questions from backend, or restore from localStorage on refresh
  useEffect(() => {
    const fetchOrRestore = async () => {
      setLoading(true);
      setError(null);

      const params = getPracticeParams();
      const hasParams = !!(params.courseId || params.subcourseId || params.skillId || params.lessonId);

      // Restore from localStorage on page reload, or when there are no API params
      if (isPageReload() || !hasParams) {
        const saved = loadSession();
        if (saved?.sessionData && saved?.questions?.length > 0) {
          setSessionData(saved.sessionData);
          setQuestions(saved.questions);
          setLoading(false);
          return;
        }
        // No saved session and no params → show error
        if (!hasParams) {
          setError('Missing required parameters. Please navigate here from a practice selection page.');
          setLoading(false);
          return;
        }
      }

      // Fresh fetch from API
      const result = await getWritingPracticeQuestions(params);

      if (result.success && result.data) {
        const subcourseId = params.subcourseId || result.data['subcourse-id'];
        const langCode = findLangCodeBySubcourseIdWithFallback(
          subcourseId,
          courseTree,
          courseTreeBaseline
        );

        const sessionDataWithParams = { ...result.data, 'lang-code': langCode };
        setSessionData(sessionDataWithParams);

        // Clear component-level answer cache for old sessions, save new session
        clearPracticeStorage(result.data['session-id'], 'writing-practice');
        const problems = result.data.problems || [];
        setQuestions(problems);
        saveSession(sessionDataWithParams, problems, location.state);
      } else {
        setError(result.message || 'Failed to load practice questions');
      }

      setLoading(false);
    };

    fetchOrRestore();
    // Intentionally depend on location only: avoid re-fetching questions when courseTree hydrates.
    // Lang resolution uses courseTree + courseTreeBaseline at fetch time; null lang is fixed by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Re-resolve and persist lang-code when session was stored with null (restore or race with empty Recoil tree).
  useEffect(() => {
    if (!sessionData) return;
    if (sessionData['lang-code']) return;
    const subId = sessionData['subcourse-id'];
    if (!subId) return;
    const resolved = findLangCodeBySubcourseIdWithFallback(subId, courseTree, courseTreeBaseline);
    if (!resolved) return;
    const next = { ...sessionData, 'lang-code': resolved };
    setSessionData(next);
    saveSession(next, questions, location.state);
  }, [sessionData, courseTree, courseTreeBaseline, questions, location.state, saveSession]);

  // Polling hook — uses ref so the first immediate poll already has the requestId
  const { data: statusData, isPolling, startPolling, stopPolling, resetData: resetPollingData } = usePolling(
    () => requestIdRef.current
      ? getPracticeStatus(requestIdRef.current, {
          page: 'WritingPractice',
          action: 'poll-status',
          practiceSessionId: sessionData?.['session-id'] || null,
        })
      : Promise.resolve(null),
    7000,
    (result) => {
      // Stop polling only if:
      // 1. Status is 'ok' AND has data (completed successfully)
      // 2. HTTP 500 — BE itself is broken (fatal, not retryable)
      // Continue polling for 'processing' or '200 + error' (retryable per async grading contract)
      if (!result) return false;

      if (result.status === 'ok' && result.data) return true;
      if (result.httpStatus >= 500) return true;
      return false;
    }
  );

  // Cleanup polling on unmount or when navigating away
  useEffect(() => {
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only cleanup on unmount - stopPolling is stable from usePolling hook

  // changed by Cursor: onSubmitError supaya error bar muncul dan user bisa "Try again" tanpa balik ke soal pertama
  const handleSubmitError = (message) => {
    setSubmissionError(message || 'Submission error. Try again');
  };

  // Submission hook
  // Submission hook
  const { handleSubmit, isSubmitting, submittedAnswers, resetSubmitting } = usePracticeSubmission({
    questions,
    sessionData,
    formatUserAnswers: formatWritingUserAnswers,
    practiceType: 'writing-practice',
    stopPolling,
    startPolling,
    setRequestId: setRequestIdWithRef,
    onSubmitError: handleSubmitError,
  });

  const isMockPolling = process.env.REACT_APP_MOCK_POLLING_ERROR_200 === 'true'
    || process.env.REACT_APP_MOCK_POLLING_ERROR_500 === 'true';

  // Handle polling result
  usePollingResult({
    statusData,
    isSubmitting,
    stopPolling,
    sessionData,
    questions,
    submittedAnswers,
    practiceType: 'writing-practice',
    location,
    navigate,
    maxPollingErrorRetries: isMockPolling ? 3 : 40,
    onPollingRetry: (count) => setPollingRetryCount(count),
    onSubmissionError: (info) => {
      resetSubmitting();
      setPollingRetryCount(0);
      const message =
        typeof info === 'string' ? info : (info?.message || '');
      setGradingFatalError({
        title: 'Grading Failed',
        message:
          message || 'An error occurred while grading your writing. Please try again.',
      });
    },
    onGradingSuccess: () => {
      clearSession();
      try { localStorage.removeItem(`writing-answers-${sessionData?.['session-id']}`); } catch (_) {}
    },
  });

  const handleClose = () => {
    clearSession();
    try { localStorage.removeItem(`writing-answers-${sessionData?.['session-id']}`); } catch (_) {}

    // Priority 1: Use fromPage from location state (from English Language Learning Page)
    const fromPage = location.state?.fromPage;
    if (fromPage) {
      console.log('📖 WritingPractice: Navigating back to fromPage:', fromPage);
      navigate(fromPage);
      return;
    }
    
    // Priority 2: Navigate back to last opened URL
    const lastOpenedUrl = getLastOpenedUrl();
    if (lastOpenedUrl) {
      console.log('📖 WritingPractice: Navigating back to lastOpenedUrl:', lastOpenedUrl);
      navigate(lastOpenedUrl);
      return;
    }
    
    // Priority 3: Fallback to default
    console.log('📖 WritingPractice: No fromPage or lastOpenedUrl found, navigating to default location');
    navigate('/learner/language-learning');
  };

  // Loading state
  if (loading) {
    return <PracticeLoadingOverlay visible text="Loading practice questions..." />;
  }

  // Error state
  if (error) {
    return <PracticeErrorState title="Writing Practice" error={error} />;
  }

  // No questions state
  if (!questions || questions.length === 0) {
    return <PracticeNoQuestionsState title="Writing Practice" onBack={handleClose} />;
  }

  // changed by Cursor: use lang-code from sessionData (resolved from courseTree) instead of hardcoding from fromPage
  const langCode = sessionData?.['lang-code'] || null;

  return (
    <Layout hideSidebar>
      <div className="quick-practice-page">
        {submissionError && (
          <div className="practice-submission-error" role="alert">
            <span>{submissionError}</span>
            <button
              type="button"
              data-track-action="writing-try-again-banner"
              className="practice-try-again-btn"
              onClick={() => {
                if (!submittedAnswers || !submittedAnswers.length) return;
                setSubmissionError(null);
                resetPollingData();
                handleSubmit(submittedAnswers);
              }}
            >
              Try again
            </button>
          </div>
        )}

        {/* Fatal grading error overlay (HTTP 500 or max retries exceeded) */}
        <PracticeLoadingOverlay
          visible={!!gradingFatalError}
          variant="error"
          text={gradingFatalError?.title || 'Grading Failed'}
          subtext={gradingFatalError?.message}
          onRetry={() => {
            if (!submittedAnswers || !submittedAnswers.length) return;
            setGradingFatalError(null);
            setPollingRetryCount(0);
            setSubmissionError(null);
            resetPollingData();
            handleSubmit(submittedAnswers);
          }}
          onClose={handleClose}
        />

        {/* Loading / retrying overlay */}
        <PracticeLoadingOverlay
          visible={isSubmitting && !gradingFatalError}
          text={pollingRetryCount > 0
            ? 'Still grading your answers...'
            : 'Submitting your answers...'}
          subtext={pollingRetryCount > 0
            ? `Encountered an issue, retrying\u2026 (attempt ${pollingRetryCount})`
            : 'Please wait while we grade your writing'}
        />

        <WritingPracticeComponent
          practiceTitle="Writing Practice" 
          key={sessionData?.['session-id'] || questions.length}
          questions={questions}
          onComplete={handleSubmit}
          onClose={handleClose}
          hideCompletedScreen={true}
          sessionData={sessionData}
          langCode={langCode}
        />
      </div>
    </Layout>
  );
};

export default WritingPractice;

